import { Table, Tag } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { ComplianceJobStatus, type ComplianceJobListItem } from '@/features/compliance/types'
import { periodEndDateToFy } from '@/features/compliance/utils'
import { JobActionsCell } from './JobActionsCell'

const STATUS_COLOR: Record<ComplianceJobStatus, string> = {
  [ComplianceJobStatus.Pending]: 'default',
  [ComplianceJobStatus.Processing]: 'processing',
  [ComplianceJobStatus.Reviewing]: 'processing',
  [ComplianceJobStatus.Parsed]: 'green',
  [ComplianceJobStatus.NeedsReview]: 'orange',
  [ComplianceJobStatus.AllMatched]: 'green',
  [ComplianceJobStatus.Failed]: 'red',
  [ComplianceJobStatus.Reviewed]: 'green',
}

const STATUS_LABEL: Record<ComplianceJobStatus, string> = {
  [ComplianceJobStatus.Pending]: ComplianceJobStatus.Pending,
  [ComplianceJobStatus.Processing]: ComplianceJobStatus.Reviewing,
  [ComplianceJobStatus.Reviewing]: ComplianceJobStatus.Reviewing,
  [ComplianceJobStatus.Parsed]: ComplianceJobStatus.NeedsReview,
  [ComplianceJobStatus.NeedsReview]: ComplianceJobStatus.NeedsReview,
  [ComplianceJobStatus.AllMatched]: 'Reviewed',
  [ComplianceJobStatus.Reviewed]: ComplianceJobStatus.Reviewed,
  [ComplianceJobStatus.Failed]: ComplianceJobStatus.Failed,
}

type ComplianceJobsTableProps = {
  jobs: ComplianceJobListItem[]
  loading: boolean
  pagination: { current: number; pageSize: number }
  total: number
  onPaginationChange: (page: number, pageSize: number) => void
  onDelete: (jobId: string) => void
  orgPlatformId?: string
}

export function ComplianceJobsTable({
  jobs,
  loading,
  pagination,
  total,
  onPaginationChange,
  onDelete,
  orgPlatformId,
}: ComplianceJobsTableProps) {
  const columns: ColumnsType<ComplianceJobListItem> = [
    {
      title: 'Financial Year',
      key: 'periodEndDate',
      dataIndex: 'periodEndDate',
      render: (_: unknown, row: ComplianceJobListItem) => periodEndDateToFy(row.periodEndDate),
    },
    {
      title: 'Upload Date',
      dataIndex: 'uploadDate',
      key: 'uploadDate',
      render: (v: string) => (v ? new Date(v).toLocaleString() : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: ComplianceJobStatus) => {
        if (status === ComplianceJobStatus.Reviewed || status === ComplianceJobStatus.AllMatched) {
          return (
            <Tag className="border-green-200 bg-green-50 text-green-700">
              {STATUS_LABEL[status]}
            </Tag>
          )
        }

        return <Tag color={STATUS_COLOR[status] ?? 'default'}>{STATUS_LABEL[status] ?? status}</Tag>
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_: unknown, row: ComplianceJobListItem) => (
        <JobActionsCell row={row} onDelete={onDelete} orgPlatformId={orgPlatformId} />
      ),
    },
  ]

  return (
    <div className="p-4">
      <Table<ComplianceJobListItem>
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={jobs}
        pagination={{
          ...pagination,
          total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '25', '50'],
          onChange: onPaginationChange,
        }}
      />
    </div>
  )
}
